<template>
  <ValidationObserver v-slot="{ invalid }" slim>
    <LoadingLayout v-if="loading"/>
    <Layout v-else class="SmsEditScheduling">

      <LayoutSection v-if="smsMessage.type === 'manual'">
        <Card>
          <CardSection>
            <validation-provider :name="$t('Date')" immediate rules="required" v-slot="{ errors }">
              <DatePicker
                :placeholder="$t('Choose a date')"
                v-model="chosenDate"
                :label="$t('sms.scheduling.dateAndTimeToSend')"
                :help-text="$t('sms.scheduling.timeToSendHelp')"
                type="datetime"
                :min-time="minTime"
                :max-time="maxTime"
                :disableDate="disableDate"
                :error="errors[0] || timingWarning"
              />
            </validation-provider>
          </CardSection>
        </Card>
        <Card v-if="countryOverrides">
          <CardSection>
            <div v-html="$t('sms.override.infoMessage', { value: countryOverrides.countryName })"/>
            <List tight class="country-overrides-list">
              <ListItem v-if="countryOverrides.days.length">{{$t('sms.override.restrictedDays')}}</ListItem>
              <ListItem v-if="countryOverrides.hoursFrom || countryOverrides.hoursTo">{{$t('sms.override.restrictedHours')}}</ListItem>
              <ListItem v-if="countryOverrides.maxMessageLength">{{$t('sms.override.maxLength')}}: {{ countryOverrides.maxMessageLength }}</ListItem>
              <ListItem v-if="countryOverrides.noMessageConcatenation">{{$t('sms.override.noMessageConcatenation')}}</ListItem>
              <ListItem v-if="countryOverrides.noUnicodeSupport">{{$t('sms.override.noUnicodeSupport')}}</ListItem>
              <ListItem v-if="countryOverrides.appendToMessage">{{$t('sms.override.appendToMessageValue', { value: countryOverrides.appendToMessage })}}</ListItem>
            </List>
          </CardSection>
        </Card>
        <Card v-if="!theUser.isDemoAccount && smsMessage.status === 'draft'">
          <CardSection>
            <Stack>
              <Button :disabled="!!timingWarning || invalid" @click="changePage('details')">{{$t('Save + Back')}}</Button>
              <Button @click="changePage('details', false)">{{$t('Back')}}</Button>
              <Button v-if="smsMessage.type === 'manual'" @click="showSendTest = true" style="margin-right: 15px;">{{$t('sms.scheduling.sendTest')}}</Button>
              <Button v-if="smsMessage.type === 'manual'" :disabled="!!timingWarning || invalid" style="margin-right: 15px;" @click="changePage('save')">{{$t('Save and Continue Later')}}</Button>
              <Button
                v-if="smsMessage.type === 'manual'"
                type="primary"
                @click="showScheduleModal = true"
                :disabled="invalid || !!timingWarning"
              >
                {{$t('sms.scheduling.scheduleToSend')}}
              </Button>
            </Stack>
          </CardSection>
        </Card>
      </LayoutSection>
    </Layout>
    <Modal
      :title="$t('sms.scheduling.sendTestMessage')"
      :visible="showSendTest"
      :primaryAction="{
        label: $t('sms.scheduling.send'),
        onAction: sendTestMessage,
        disabled: testError
      }"
      @close="closeSendTest"
    >
      <TextField
        prefix="+"
        :placeholder="$t('sms.scheduling.phoneNumber')"
        v-model="testPhoneNumber"
        :error="testError"
      />
    </Modal>
    <Modal
      :title="$t('sms.scheduling.scheduleToSend')"
      :visible="showScheduleModal"
      :primaryAction="{
        label: $t('sms.scheduling.scheduleToSend'),
        onAction: send,
        disabled: !targetedCount || !pricePerMessage
      }"
      :loading="loadingCounts"
      @close="closeScheduleModal"
    >
      <!-- <span v-if="true" class="spinner"><Spinner size="large"/>&nbsp;</span> -->
      <div class="price-value">{{$t('sms.scheduling.fanplayrPricePerMessage')}}: {{`${getCurrencySymbol(theUser.accountCurrency)}${pricePerMessage || '0.00'}`}}</div>
      <div class="price-value">{{$t('sms.scheduling.totalMessages')}}: <Formatter type="integer" :value="targetedCount"/></div>
      <div class="price-value">{{$t('sms.scheduling.totalPriceApprox')}}: <Formatter type="currency" :currency="theUser.accountCurrency" :value="pricePerMessage * targetedCount"/></div>
      <Checkbox v-if="targetedCount && pricePerMessage" :label="$t('sms.scheduling.acceptScheduleMessage')" v-model="acceptPrice"/>
      <InlineError v-else-if="!loadingCounts" :message="$t('sms.scheduling.noMessageScheduleError')"/> 
    </Modal>
    <!-- <pre>{{currencies}}</pre> -->
  </ValidationObserver>

</template>

<script>

import moment from 'moment'
import _ from 'lodash'
import currencies from '@/../data/Currencies'
// import TimezoneUtil from '@/../../shared/TimezoneUtil'

window.moment = moment

export default {
  name: 'SmsEditScheduling',
  props: {
    smsMessage: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showSendTest: false,
      testPhoneNumber: null,

      smsSettings: null,
      countryOverrides: null,
      chosenDate: '',

      copyDate: null,
      copyTime: null,

      currencies: currencies,

      showScheduleModal: false,

      loadingCounts: true,
      loading: true,
      targetedCount: 0,
      pricePerMessage: 0,
      acceptPrice: false
    }
  },
  mounted() {
    const scheduledDate = this.smsMessage.scheduledDate || ''
    const scheduledTime = this.smsMessage.scheduledTime || ''
    //
    if (this.smsMessage.type === 'manual') {
      this.chosenDate = scheduledDate ? `${scheduledDate} ${scheduledTime}` : ''
    }

    this.copyDate = _.cloneDeep(scheduledDate)
    this.copyTime = _.cloneDeep(scheduledTime)

    NProgress.start()
    Promise.all([
      this.api.getSmsSettings(),
      this.api.getSmsPricePerMessage(this.smsMessage.siteId, this.theUser.accountCurrency),
      this.api.getSmsCountryOverrides(this.smsMessage.siteId)
    ])
      .then((data) => {
        const [settings, price, countryOverrides] = data
        this.smsSettings = settings || {}
        this.pricePerMessage = price
        this.countryOverrides = countryOverrides
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })

    this.api.getSmsMessageCount(this.smsMessage)
      .then((data) => {
        this.targetedCount = data
        this.loadingCounts = false
      })
      .catch(this.apiCatch)
  },
  computed: {
    noEditing() {
      return this.smsMessage.type === 'manual' && this.smsMessage.status !== 'draft'
    },
    minTime() {
      const times = []
      if (this.smsSettings && this.smsSettings.settings.hoursFrom) {
        times.push(this.smsSettings.settings.hoursFrom.toString())
      }
      if (this.countryOverrides && this.countryOverrides.hoursFrom) {
        times.push(this.countryOverrides.hoursFrom.toString())
      }
      const orderedTimes = times.sort().reverse()
      return orderedTimes.length && orderedTimes[0] || '0:00'
    },
    maxTime() {
      const times = []
      if (this.smsSettings && this.smsSettings.settings.hoursTo) {
        times.push(this.smsSettings.settings.hoursTo.toString())
      }
      if (this.countryOverrides && this.countryOverrides.hoursTo) {
        times.push(this.countryOverrides.hoursTo.toString())
      }
      const orderedTimes = times.sort()
      return orderedTimes.length && orderedTimes[0] || '23:59'
    },
    testError() {
      const regex = /^\d+$/
      if (this.testPhoneNumber && !this.testPhoneNumber.match(regex)) {
        return this.$t('sms.scheduling.invalidPhoneNumber')
      }
      return false
    },
    timingWarning() {
      if (!this.smsSettings) {
        return ''
      }
      const date = moment(this.chosenDate)
      const time = date.format('HH:mm:ss')
      if (this.smsSettings.settings.hoursFrom) {
        if (time < this.smsSettings.settings.hoursFrom) {
          return this.$t('sms.scheduling.earlyTimeWarning', {value: this.smsSettings.settings.hoursFrom})
        }
      }
      if (this.smsSettings.settings.hoursTo) {
        if (time > this.smsSettings.settings.hoursTo) {
          return this.$t('sms.scheduling.lateTimingWarning', {value: this.smsSettings.settings.hoursTo})
        }
      }
      const dayOfWeek = date.day()
      if (this.smsSettings.settings.days && !this.smsSettings.settings.days.includes(dayOfWeek)) {
        return this.$t('sms.scheduling.dayWarning', {value: this.niceDays(this.smsSettings.settings.days)})
      }
      if (this.countryOverrides) {
        if (this.countryOverrides.hoursFrom && this.countryOverrides.hoursFrom > time) {
          return this.$t('sms.scheduling.earlyTimeWarningCountry', { value: this.countryOverrides.hoursFrom })
        }
        if (this.countryOverrides.hoursTo && this.countryOverrides.hoursTo < time) {
          return this.$t('sms.scheduling.lateTimingWarningCountry', { value: this.countryOverrides.hoursTo })
        }
        if (this.countryOverrides.days.length && !this.countryOverrides.days.includes(dayOfWeek)) {
          return this.$t('sms.scheduling.dayWarningCountry', { value: this.niceDays(this.countryOverrides.days) })
        }
      }
      return ''
    }
  },
  methods: {
    niceDays(days) {
      return days.map(n => [this.$t('Sunday'), this.$t('Monday'), this.$t('Tuesday'), this.$t('Wednesday'), this.$t('Thursday'), this.$t('Friday'), this.$t('Saturday')][n]).join(', ')
    },
    getCurrencySymbol(currency) {
      const curr = this.currencies.find(c => c.key === currency)
      return curr && curr.symbol || ''
    },
    disableDate(date) {
      if (!this.smsSettings) {
        return false
      }
      const dayOfWeek = date.day()
      if (this.smsSettings.settings.days && !this.smsSettings.settings.days.includes(dayOfWeek)) {
        return true
      }
      if (this.countryOverrides && this.countryOverrides.days.length && !this.countryOverrides.days.includes(dayOfWeek)) {
        return true
      }
      return false
    },
    async changePage(nextPage, forceSave = true) {
      const date = this.smsMessage.type === 'manual' && this.chosenDate ? this.chosenDate.split(' ') : ['', '']
      if (forceSave) {
        this.$emit('update', {
          nextPage,
          date: date[0],
          time: date[1]
        })
      } else {
        if (!_.isEqual({ date: date[0], time: date[1] }, { date: this.copyDate, time: this.copyTime })) {
          const res = await this.$ui.confirm({
            title: this.$t('Unsaved Changes'),
            content: this.$t('messaging.loseChangesMessage'),
            primaryActionLabel: this.$t('Discard changes'),
            primaryActionType: 'destructive',
            secondaryActionLabel: this.$t('Continue editing')
          })
          if (!res) return;
        }
        this.$emit('goToPage', nextPage)
      }
    },
    send() {
      this.changePage('send')
    },
    closeSendTest() {
      this.showSendTest = false
      this.testPhoneNumber = null
    },
    closeScheduleModal() {
      this.showScheduleModal = false
      this.acceptPrice = false
    },
    sendTestMessage() {
      NProgress.start()
      this.showSendTest = false
      this.api.sendTestSmsMessage(this.smsMessage.smsId, this.testPhoneNumber)
        .then(() => {
          this.$ui.toast({
            title: this.$t('sms.scheduling.messageSent')
          })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.closeSendTest()
          NProgress.done()
        })
    }
  },
  components: {
  }
};
</script>

<style lang="less" scoped>
  .info {
    background: #eee;
    padding: 10px;
    font-size: 90%;
    margin-bottom: 20px;
  }
  .btn-back-only {
    margin-left: 15px;
  }
  .price-value {
    padding: 5px 0px 5px 0px;
  }
  .spinner {
    float: right;
  }
  .country-overrides-list {
    padding-top: 5px;
  }
</style>
